import { FormProvider, UseFormReturn, useWatch } from "react-hook-form";
import { useTranslation } from "../../core/translation.core";
import { ProductFormData, ProductSearchRst2 } from "../../types/product";
import { LabelAndInput } from "../reuseables/form.comp";
import TrashIcon from "../../icons/trash.icon";
import { useQuery } from "@tanstack/react-query";
import productCore, { getProduct } from "../../core/product.core";
import localeCore from "../../core/locale.core";
import { useEffect, useRef, useState } from "react";
import ChevronDownIcon from "../../icons/chevronDown.icon";
import { inputCssNoMy } from "../reuseables/native.comp";
import LoadingOvalCenterIcon from "../../icons/loadingOvalCenter.icon";

interface BoxConversionEditorProps {
  methods: UseFormReturn<any>;
}

export default function BoxConversionEditor({ methods }: BoxConversionEditorProps) {
  const { t, msg } = useTranslation();

  const remove = () => {
    methods.setValue("boxConversion.productId", "");
    methods.setValue("boxConversion.conversionRate", "");
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-base font-semibold">{t(msg.product.boxConversion)}</h3>
      </div>

      <div className="flex gap-4 items-end box-conversion-row">
        <div className="flex-1">
          <ProductSearchSelector methods={methods} />
        </div>
        <div className="flex-1">
          <LabelAndInput
            register={methods.register}
            fieldName="boxConversion.conversionRate"
            label={t(msg.product.conversionRate)}
            type="number"
            step="1"
            registerOptions={{ min: 1 }}
            labelCss="text-xs"
          />
        </div>
        <button
          type="button"
          onClick={remove}
          className="mb-2 text-red-600 hover:text-red-800 delete-btn"
          title={t(msg.product.removeBoxConversion)}
        >
          <TrashIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

function ProductSearchSelector({ methods }: { methods: UseFormReturn<ProductFormData> }) {
  const [isOpened, setIsOpened] = useState(false);
  const [searchText, setSearchText] = useState<string>("");
  const ref = useRef<HTMLDivElement>(null);
  const { t, msg } = useTranslation();

  const productId = useWatch({ control: methods.control, name: "boxConversion.productId" });

  const { data: products = { list: [], size: 0 } as ProductSearchRst2 } = useQuery({
    queryKey: [searchText],
    queryFn: (context) => productCore.searchProduct(context.queryKey[0] as string, false, true),
    enabled: searchText !== "",
  });

  useEffect(() => {
    function onClick(e: MouseEvent) {
      // This is a hack to get the latest state
      if (!ref || !ref.current) return;
      const isOpened = (ref.current as any).getAttribute("data-state");

      if (!isOpened) return;

      const isWithin = (e.target as any).closest("#box-conversion-productId");

      if (!isWithin) {
        setIsOpened(false);
      }
    }

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  const saveProduct = (productId: string) => {
    methods.setValue("boxConversion.productId" as any, productId);
  };

  const css =
    "relative " +
    "h-9 w-full " +
    "dark:bg-her-backblue " +
    `border rounded-t-md ${
      !isOpened ? "rounded-b-md" : ""
    } hover:border-blue-200 hover:border-2 focus-visible:border-blue-600 focus-visible:border-2 ` +
    "focus-visible:outline-none outline-none " +
    "focus-visible:shadow-sm focus-visible:shadow-blue-800 " +
    "duration-1000 transition-[box-shadow] " +
    "disabled:text-her-lightgray2 text-base ";

  return (
    <div className="basis-full gap-2">
      <h2>{t(msg.product.filterProducts)}</h2>
      <FormProvider {...methods}>
        <div id="box-conversion-productId" ref={ref} data-state={isOpened} className={css}>
          <button
            type="button"
            className="w-full h-full flex justify-between pr-2 pl-4 items-center"
            onClick={() => setIsOpened((value) => !value)}
          >
            <CurrentProduct productId={productId} /> <ChevronDownIcon />
          </button>
          {isOpened && <Panel products={products.list} onSearch={(value) => setSearchText(value)} saveProduct={saveProduct} />}
        </div>
      </FormProvider>
    </div>
  );
}

export const CurrentProduct = ({ productId }: { productId: string }) => {
  const { t, msg } = useTranslation();
  const { data: product, isLoading } = useQuery({
    queryKey: ["singleProduct", productId],
    queryFn: (context) => getProduct(context.queryKey[1]),
    enabled: !!productId,
  });

  if (isLoading) return <div>...</div>;
  if (!product) return <div>{t(msg.product.selectProduct)}</div>;

  return <div>{product.nameLocale}</div>;
};

export const Panel = ({ products, onSearch, saveProduct }) => {
  return (
    <div
      id="box-panel"
      className="absolute top-[34px] left-0 w-full border rounded-b-md hover:border-blue-200 bg-white dark:bg-her-backblue space-y-1 px-4 py-4 max-h-72 overflow-y-auto z-50"
    >
      <SearchBox onSearch={onSearch} />
      <div id="box-panel-list">
        {products.map((p) => (
          <div
            className="w-full text-left cursor-pointer"
            key={p.id}
            onClick={() => saveProduct(p.id)}
            data-value={localeCore.getLocaleValueDefault(p.name)}
            data-sku={p.sku}
          >
            {localeCore.getLocaleValueDefault(p.name)}
          </div>
        ))}
      </div>
    </div>
  );
};

export const SearchBox = ({ onSearch }: { onSearch: (text: string) => void }) => {
  const ref = useRef<HTMLInputElement>(null);
  const { t, msg } = useTranslation();

  return (
    <div className="flex gap-4 items-center">
      <input
        id="product-search"
        ref={ref}
        className={`${inputCssNoMy} grow`}
        placeholder={t(msg.product.searchProduct)}
        type="text"
        onChange={(e) => {
          onSearch(e.target.value);
        }}
      />
    </div>
  );
};
